import {
  useTheme,
  Box,
  Avatar,
  Dialog,
  Link,
  IconButton,
  Tooltip,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { API_GET, API_PATCH, DBRequest, useConfig } from '../../api/api';
import { tokens } from '../../global/theme/tokens';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import ExoTable from '../../components/exo/ExoTable';
import { OrderStatus } from '../../components/special/Status';
import NewOrder from '../../components/new/NewOrder';
import ExtendOrder from './extendOrder';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../../components/special/Header';
import ExoTableRecipient from '../../components/exo/ExoTableRecipient';
import { useTranslation } from 'react-i18next';
import useFetch from '../../api/useFetch';
import { recipientTypeMap } from '../../components/routing/routingMaps';
import DateLabel from '../../components/label/DateLabel';
import { LeadStatusSelector } from '../leads/status/LeadStatusSelector';
import { updateOrCreate } from '../../components/special/updateOrCreate';
import OpenInBrowserIcon from '@mui/icons-material/OpenInBrowser';
import MoneyLabel from '../../components/label/MoneyLabel';
import DocumentOverviewCard from '../../components/document-overview/DocumentOverviewCard';
import { buildPermanentFilter } from '../../components/document-overview/buildPermanentFilter';
const Orders = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const config = useConfig();

  const navigate = useNavigate();

  // Filter state synced with DocumentOverviewCard. null until the card reports
  // its (persisted) filter — keeps the table fetch from firing twice on load.
  const [overviewFilters, setOverviewFilters] = useState(null);
  const permanentFilter = overviewFilters
    ? buildPermanentFilter({ ...overviewFilters, dateField: 'startDate' })
    : '';
  const fetchPath =
    overviewFilters === null
      ? null
      : permanentFilter
        ? `orders?${permanentFilter}`
        : 'orders';

  const { apiData, isLoading, setApiData } = useFetch(fetchPath);
  const { offerId } = useParams();

  const [anchorEl, setAnchorEl] = useState(null);
  const [itemToChange, setItemToChange] = useState(null);
  const handleStatusClick = (event, item) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
    setItemToChange(item);
  };
  function handleStatusChange(status, item) {
    const request = { status: status.status };

    DBRequest({
      config,
      path: `orders/${item.id}/status`,
      method: API_PATCH(request),
      onResponse: () =>
        setApiData({
          ...apiData,
          data: updateOrCreate(apiData.data, {
            ...item,
            status: status.status,
          }),
        }),
    });
  }

  // table
  const Columns = [
    {
      header: t('Nr'), // header of column
      key: 'nr', // key of column in row-object
      thAlign: 'left', // header text alignment
      tdAlign: 'left', // cell text alignment
      sortable: true, // enable sort function for column
      cell: ({ row }) => (
        <Box className="monospace whitespace-nowrap">
          {row.nr ?? t('Draft')}
        </Box>
      ),
    },
    {
      header: t('Status'),
      key: 'status',
      thAlign: 'left', // header text alignment
      tdAlign: 'left', // cell text alignment
      sortable: true, // enable sort function for column
      cell: ({ row }) => (
        <OrderStatus
          number={row.status}
          onClick={(e) => handleStatusClick(e, row)}
        />
      ), // custom xml for cells in the column. you get the row as object
    },
    {
      header: t('Start Date'),
      key: 'startDate',
      thAlign: 'left', // header text alignment
      tdAlign: 'left', // cell text alignment
      sortable: true, // enable sort function for column
      hiddenMobile: true,
      cell: ({ row }) => <DateLabel date={row.startDate}></DateLabel>,
    },
    {
      header: t('Title'),
      key: 'title',
      width: '100%',
      thAlign: 'left', // header text alignment
      tdAlign: 'left', // cell text alignment
      sortable: true, // enable sort function for column
      onClick: (row) => navigate(`/orders/${row.id}`),
    },
    {
      header: t('Project'),
      key: 'project',
      thAlign: 'left',
      tdAlign: 'left',
      hiddenMobile: true,
      cell: ({ row }) => (
        <Box className="whitespace-nowrap">
          {row.project ? `${row.project.nr ?? ''} ${row.project.name}`.trim() : ''}
        </Box>
      ),
      onClick: (row) => row.project && navigate(`/projects/${row.project.id}`),
    },
    {
      header: t('Client'),
      key: 'recipient',
      thAlign: 'left', // header text alignment
      tdAlign: 'left', // cell text alignment
      width: '270px',
      sortable: true, // enable sort function for column
      hiddenMobile: true,
      cell: ({ row }) => <ExoTableRecipient row={row} />, // custom xml for cells in the column. you get the row as object
      onClick: (row) =>
        navigate(`/${recipientTypeMap[row.recipientType]}/${row.recipient.id}`),
    },
    {
      header: t('netto'),
      key: 'nettoVolume',
      thAlign: 'right', // header text alignment
      tdAlign: 'right', // cell text alignment
      cell: ({ row }) => <MoneyLabel money={row.volume} />,
      sortable: true, // enable sort function for column
    },
    {
      header: t('tax'),
      key: 'taxVolume',
      thAlign: 'right', // header text alignment
      tdAlign: 'right', // cell text alignment
      cell: ({ row }) => <MoneyLabel money={row.taxVolume} />,
      sortable: true, // enable sort function for column
    },
    {
      header: t('brutto'),
      key: 'volume',
      thAlign: 'right', // header text alignment
      tdAlign: 'right', // cell text alignment
      cell: ({ row }) => <MoneyLabel money={row.taxVolume + row.volume} />,
      sortable: true, // enable sort function for column
    },
    {
      header: t('open netto'),
      key: 'openVolume',
      thAlign: 'right', // header text alignment
      tdAlign: 'right', // cell text alignment
      cell: ({ row }) => <MoneyLabel money={row.openVolume} />,
      sortable: true, // enable sort function for column
    },
    {
      header: t('open brutto'),
      key: 'openVolumeBrutto',
      thAlign: 'right', // header text alignment
      tdAlign: 'right', // cell text alignment
      cell: ({ row }) => (
        <MoneyLabel money={row.openTaxVolume + row.openVolume} />
      ),
    },
    {
      header: t('skonto'),
      key: 'skonto',
      thAlign: 'right', // header text alignment
      tdAlign: 'right', // cell text alignment
      className: 'w-full lg:w-auto',
      cell: ({ row }) => {
        const brutto = row.taxVolume + row.volume;
        return (
          <MoneyLabel
            money={
              row.discount
                ? brutto - brutto * (parseFloat(row.discountRate) / 100)
                : brutto
            }
          />
        );
      },
      sortable: true, // enable sort function for column
    },
    {
      header: '',
      key: 'link',
      thAlign: 'right', // header text alignment
      tdAlign: 'right', // cell text alignment
      className: 'w-full lg:w-auto',
      cell: ({ row }) => (
        <Tooltip title={t('Open in new window')}>
          <Link href={`/orders/${row.id}`} target="_blank">
            <IconButton>
              <OpenInBrowserIcon />
            </IconButton>
          </Link>
        </Tooltip>
      ),
    },
  ];

  const filters = {
    filter: {
      label: t('Filter'),
      items: [
        {
          type: 'dropdown',
          label: t('Status'),
          key: 'state2',
          baseValue: 'status[eq]=',
          active: false,
          options: [
            {
              label: t('Created'),
              value: 0,
            },
            {
              label: t('Finished'),
              value: 1,
            },
            {
              label: t('Canceled'),
              value: 2,
            },
            {
              label: t('Sent'),
              value: 3,
            },
          ],
        },
      ],
    },
  };

  useEffect(() => {
    if (offerId) {
      handleNewOrder();
    }
  }, [offerId]);

  // actions
  const [openNewOrder, setOpenNewOrder] = useState(false);
  function handleNewOrder() {
    setOpenNewOrder(true);
  }
  return (
    <>
      <Header title={t('Orders')} />

      <DocumentOverviewCard
        resource="orders"
        preferencesKey="orders"
        onFilterChange={setOverviewFilters}
        actions={[
          {
            primary: true,
            label: t('New Order'),
            icon: <NoteAddIcon fontSize="small" />,
            onClick: handleNewOrder,
          },
        ]}
      />

      <ExoTable
        data={apiData}
        isLoading={!Boolean(apiData)}
        columns={Columns}
        extendElement={({ row, onUpdate }) => (
          <ExtendOrder row={row} onUpdate={onUpdate} />
        )}
        filter={permanentFilter}
        filters={filters}
        columnSettingsKey="orders"
        defaultHiddenColumns={[
          'taxVolume',
          'volume',
          'validUntil',
          'skonto',
          'openVolumeBrutto',
          'nettoVolume',
          'project',
        ]}
      />

      <NewOrder
        open={openNewOrder}
        onClose={() => setOpenNewOrder(false)}
        orders={apiData}
        setOrders={setApiData}
        offerId={offerId}
      />
      <LeadStatusSelector
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        onStatusChange={(status) => handleStatusChange(status, itemToChange)}
        customStatuses={[
          {
            label: t('Created'),
            status: 0,
            variant: t('open'),
          },
          {
            label: t('Sent'),
            status: 3,
            variant: t('sent'),
          },
          {
            label: t('Finished'),
            status: 1,
            variant: t('finished'),
          },
          {
            label: t('Canceled'),
            status: 2,
            variant: t('rejected'),
          },
        ]}
      />
    </>
  );
};

export default Orders;
